import { runtimeConfig } from "@src/runtime-config";
import { useGenerateRoutes } from "@router/router-helper";
import { useNavigate } from "react-router-dom";
import {
  NavigateWithPreservedParams,
  UseAppNavigation,
} from "@router/use-app-navigation-types";
import { getEntryPageUrl } from "@utils/url-utils";

/**
 * A custom hook that calls the history and wraps different navigation functions
 * Returns functions that actually navigate using react-router-dom's navigate() - they perform the navigation
 */
export function useAppNavigation(): UseAppNavigation {
  const navigate = useNavigate();
  const {
    generateHomeRoute,
    generateMemberRoute,
    generateCompanyAddDevicePlanRoute,
    generateCompanyAddPlanRoute,
    generateCompanyAddFeatureRoute,
    generateCompanyAddProjectPlanRoute,
    generateCompanyCopyPlanRoute,
    generateCompanyRoute,
    generateProjectAddPlanRoute,
    generateProjectAddFeatureRoute,
    generateProjectCopyPlanRoute,
    generateProjectRoute,
    generateAboutProductsRoute,
    generateTestingImpersonationRoute,
  } = useGenerateRoutes();

  /**
   * Navigates to a specified path while preserving navigation parameters.
   *
   * @param {NavigateWithPreservedParams} params - The navigation parameters.
   * @param {object} params.path - The target path and any associated parameters.
   * @param {string} [params.state="in-app-navigation"] - Optional navigation state to be preserved.
   *
   * @returns {void}
   */
  function navigateWithPreservedParams({
    path,
    state = "in-app-navigation",
  }: NavigateWithPreservedParams): void {
    navigate(
      {
        ...path,
      },
      { state }
    );
  }

  function navigateToPlanCreation({
    subjectType,
    subjectId,
    creationMode = "subscription",
  }: Parameters<UseAppNavigation["navigateToPlanCreation"]>[0]): void {
    switch (subjectType) {
      case "COMPANY_MEMBER":
      case "COMPANY": {
        navigateWithPreservedParams({
          path:
            creationMode === "feature"
              ? generateCompanyAddFeatureRoute({ companyId: subjectId })
              : generateCompanyAddPlanRoute({ companyId: subjectId }),
        });
        break;
      }
      case "DEVICE": {
        navigateWithPreservedParams({
          path: generateCompanyAddDevicePlanRoute({ companyId: subjectId }),
        });
        break;
      }
      case "PROJECT": {
        navigateWithPreservedParams({
          path:
            creationMode === "feature"
              ? generateProjectAddFeatureRoute({ projectId: subjectId })
              : generateProjectAddPlanRoute({ projectId: subjectId }),
        });
        break;
      }

      default: {
        throw new Error(
          `navigateToPlanCreation: Unsupported subjectType ${subjectType}`
        );
      }
    }
  }

  function navigateToPlanCopy({
    plan,
  }: Parameters<UseAppNavigation["navigateToPlanCopy"]>[0]): void {
    switch (plan.subject.type) {
      case "COMPANY": {
        navigateWithPreservedParams({
          path: generateCompanyCopyPlanRoute({
            companyId: plan.subject.id,
            planId: plan.id,
          }),
        });
        break;
      }
      case "PROJECT": {
        navigateWithPreservedParams({
          path: generateProjectCopyPlanRoute({
            projectId: plan.subject.id,
            planId: plan.id,
          }),
        });
        break;
      }

      default: {
        throw new Error(
          `navigateToPlanCopy: Unsupported subjectType ${plan.subject.type}`
        );
      }
    }
  }

  function navigateToProjectCreation(
    subject: Parameters<UseAppNavigation["navigateToProjectCreation"]>[0]
  ): void {
    navigateWithPreservedParams({
      path: generateCompanyAddProjectPlanRoute({
        companyId: subject.companyId,
        groupId: subject.groupId,
      }),
    });
  }

  function navigateToProfilePage({
    subjectType,
    subjectId,
    tab,
  }: Parameters<UseAppNavigation["navigateToProfilePage"]>[0]): void {
    switch (subjectType) {
      case "COMPANY":
      case "DEVICE": {
        navigateWithPreservedParams({
          path: generateCompanyRoute({
            companyId: subjectId,
            companyProfileTab: tab,
          }),
        });
        break;
      }

      case "PROJECT": {
        navigateWithPreservedParams({
          path: generateProjectRoute({
            projectId: subjectId,
            projectProfileTab: tab,
          }),
        });
        break;
      }

      case "COMPANY_MEMBER":
      case "USER": {
        navigateWithPreservedParams({
          path: generateMemberRoute({
            memberId: subjectId,
            memberProfileTab: tab,
          }),
        });
        break;
      }

      default: {
        // eslint-disable-next-line no-console -- No need to show this to user, even though worth share it in console
        console.warn(
          `navigateToProfilePage not implemented for ${subjectType} yet`
        );
        break;
      }
    }
  }

  function navigateToWorkspacePage(): void {
    globalThis.location.href = runtimeConfig.urls.dashboardUrl;
  }

  function navigateToHomePage(): void {
    navigateWithPreservedParams({
      path: generateHomeRoute(),
    });
  }

  function navigateToLoginPage(): void {
    globalThis.location.href = getEntryPageUrl({});
  }

  function navigateToLogoutPage(): void {
    globalThis.location.href = getEntryPageUrl({
      path: "/logout",
    });
  }

  function navigateToAboutProductsPage(): void {
    navigateWithPreservedParams({
      path: generateAboutProductsRoute(),
    });
  }

  function navigateToTestingImpersonationPage(): void {
    navigateWithPreservedParams({
      path: generateTestingImpersonationRoute(),
    });
  }

  return {
    navigateToPlanCreation,
    navigateToPlanCopy,
    navigateToProjectCreation,
    navigateToProfilePage,
    navigateToWorkspacePage,
    navigateToLoginPage,
    navigateToLogoutPage,
    navigateToHomePage,
    navigateToAboutProductsPage,
    navigateToTestingImpersonationPage,
  };
}
